/**
 * Analytics derivations.
 *
 * Pure functions over the flat record set, kept out of the components so the
 * numbers behind every tile and chart are unit-testable.
 *
 * Two conventions run through this file:
 *  - *Transaction* figures (premium, tax) sum every row, so an endorsement adds
 *    and a cancellation subtracts, matching how a premium bordereau reconciles.
 *  - *Policy* figures (counts, lives, limits) use only `new` and `renewal` rows,
 *    the base transactions, so a policy is never counted twice.
 */

import { POLICY_STATUS_ORDER, STATUS_META } from '../PolicyAdmin/constants';
import type { PolicyStatus } from '../PolicyAdmin/types';
import type {
  AnalyticsKpis,
  DimensionSlice,
  PolicyReportRecord,
  RenewalOutlookItem,
  ReportingAnalytics,
  TrendPoint,
} from './types';
import { daysUntil, formatMonthLabel, monthsBetween } from './utils';

const RISK_TIER_LABELS: Record<string, string> = {
  preferred: 'Preferred',
  standard: 'Standard',
  substandard: 'Substandard',
};

/** Days ahead included in the renewal outlook list. */
const RENEWAL_HORIZON_DAYS = 90;
const RENEWAL_OUTLOOK_LIMIT = 8;
/** Dimensions are long-tailed; anything past this is folded into "Other". */
const DIMENSION_LIMIT = 6;

export const isBaseTransaction = (record: PolicyReportRecord): boolean =>
  record.transactionType === 'new' || record.transactionType === 'renewal';

/** Premium net of brokerage and coverholder commission — what settles to the market. */
export function netPremiumOf(record: PolicyReportRecord): number {
  const deductionRate = (record.brokerageRate + record.coverholderCommissionRate) / 100;
  return record.grossWrittenPremium * (1 - deductionRate);
}

function sumBy<T>(items: T[], value: (item: T) => number): number {
  return items.reduce((total, item) => total + value(item), 0);
}

/**
 * Note on retention: true renewal retention is deliberately not reported here.
 * The record set is filtered by written date, and the expiring term a renewal
 * replaces was written a year or more earlier — so it usually sits outside the
 * window and the ratio would read as near-zero retention on a healthy book.
 * `renewalSharePct` answers the question the filtered data can actually support:
 * how much of the premium written in this period came from renewals.
 */
function computeKpis(records: PolicyReportRecord[]): AnalyticsKpis {
  const base = records.filter(isBaseTransaction);
  const inForce = base.filter(r => r.status === 'active');
  const lost = base.filter(r => r.status === 'cancelled');

  const grossWrittenPremium = sumBy(records, r => r.grossWrittenPremium);
  const basePremium = sumBy(base, r => r.grossWrittenPremium);
  const livesCovered = sumBy(inForce, r => r.employeeCount);

  // Weight the loss ratio by premium so a small substandard risk cannot drag the
  // book-level number around. Negative rows (returns) are excluded.
  const ratedPremium = sumBy(
    base.filter(r => r.grossWrittenPremium > 0),
    r => r.grossWrittenPremium
  );
  const weightedLossRatioPct = ratedPremium
    ? sumBy(
        base.filter(r => r.grossWrittenPremium > 0),
        r => r.grossWrittenPremium * r.lossRatioPct
      ) / ratedPremium
    : 0;

  const lostPremium = sumBy(lost, r => Math.abs(r.grossWrittenPremium));
  const newBusinessPremium = sumBy(
    base.filter(r => r.transactionType === 'new'),
    r => r.grossWrittenPremium
  );
  const renewalPremium = sumBy(
    base.filter(r => r.transactionType === 'renewal'),
    r => r.grossWrittenPremium
  );

  return {
    grossWrittenPremium,
    netPremium: sumBy(records, netPremiumOf),
    taxesAndFees: sumBy(records, r => r.taxesAndFees),
    transactionCount: records.length,
    policyCount: new Set(base.map(r => r.policyId)).size,
    inForceCount: inForce.length,
    inForcePremium: sumBy(inForce, r => r.grossWrittenPremium),
    livesCovered,
    averagePremium: base.length ? basePremium / base.length : 0,
    averagePremiumPerLife: livesCovered
      ? sumBy(inForce, r => r.grossWrittenPremium) / livesCovered
      : 0,
    totalAggregateLimit: sumBy(inForce, r => r.aggregateLimit),
    weightedLossRatioPct,
    newBusinessPremium,
    renewalPremium,
    renewalSharePct:
      newBusinessPremium + renewalPremium
        ? (renewalPremium / (newBusinessPremium + renewalPremium)) * 100
        : null,
    cancellationCount: lost.length,
    cancelledPremium: lostPremium,
  };
}

/** Premium and transaction count per month, with empty months filled in as zero. */
function computeTrend(
  records: PolicyReportRecord[],
  fromDate: string,
  toDate: string
): TrendPoint[] {
  const buckets = new Map<string, TrendPoint>();
  monthsBetween(fromDate, toDate).forEach(month => {
    buckets.set(month, {
      month,
      label: formatMonthLabel(month),
      grossWrittenPremium: 0,
      transactionCount: 0,
    });
  });

  records.forEach(record => {
    let bucket = buckets.get(record.reportingMonth);
    if (!bucket) {
      // A record can sit outside the requested window if the caller passed a
      // narrower range than the data; keep it rather than silently dropping premium.
      bucket = {
        month: record.reportingMonth,
        label: formatMonthLabel(record.reportingMonth),
        grossWrittenPremium: 0,
        transactionCount: 0,
      };
      buckets.set(record.reportingMonth, bucket);
    }
    bucket.grossWrittenPremium += record.grossWrittenPremium;
    bucket.transactionCount += 1;
  });

  return [...buckets.values()].sort((a, b) => a.month.localeCompare(b.month));
}

/**
 * Group records into premium slices. `limit` folds the tail into an "Other" row
 * so a bar list stays readable; pass 0 to keep every group.
 */
function groupBy(
  records: PolicyReportRecord[],
  keyOf: (record: PolicyReportRecord) => string,
  labelOf: (record: PolicyReportRecord) => string,
  limit = DIMENSION_LIMIT
): DimensionSlice[] {
  const groups = new Map<string, { label: string; premium: number; count: number }>();
  records.forEach(record => {
    const key = keyOf(record);
    const existing = groups.get(key) ?? { label: labelOf(record), premium: 0, count: 0 };
    existing.premium += record.grossWrittenPremium;
    existing.count += 1;
    groups.set(key, existing);
  });

  const total = sumBy([...groups.values()], g => g.premium);
  const share = (premium: number) => (total ? (premium / total) * 100 : 0);

  const slices = [...groups.entries()]
    .map(([key, group]) => ({
      key,
      label: group.label,
      premium: group.premium,
      count: group.count,
      sharePct: share(group.premium),
    }))
    .sort((a, b) => b.premium - a.premium);

  if (!limit || slices.length <= limit) return slices;

  const head = slices.slice(0, limit);
  const tail = slices.slice(limit);
  head.push({
    key: '__other__',
    label: `Other (${tail.length})`,
    premium: sumBy(tail, s => s.premium),
    count: sumBy(tail, s => s.count),
    sharePct: share(sumBy(tail, s => s.premium)),
  });
  return head;
}

/** Status mix keeps the workflow order rather than sorting by size. */
function groupByStatus(records: PolicyReportRecord[]): DimensionSlice[] {
  const slices = groupBy(
    records,
    r => r.status,
    r => STATUS_META[r.status].label,
    0
  );
  const orderOf = (key: string) => POLICY_STATUS_ORDER.indexOf(key as PolicyStatus);
  return slices.sort((a, b) => orderOf(a.key) - orderOf(b.key));
}

function computeRenewalOutlook(records: PolicyReportRecord[], today: string): RenewalOutlookItem[] {
  return records
    .filter(r => (r.status === 'active' || r.status === 'issued') && !r.cancellationDate)
    .map(r => ({
      policyId: r.policyId,
      policyNumber: r.policyNumber,
      insuredName: r.insuredName,
      expirationDate: r.expirationDate,
      daysToExpiry: daysUntil(today, r.expirationDate),
      grossWrittenPremium: r.grossWrittenPremium,
      status: r.status,
    }))
    .filter(item => item.daysToExpiry >= 0 && item.daysToExpiry <= RENEWAL_HORIZON_DAYS)
    .sort((a, b) => a.daysToExpiry - b.daysToExpiry)
    .slice(0, RENEWAL_OUTLOOK_LIMIT);
}

export function computeAnalytics(
  records: PolicyReportRecord[],
  range: { fromDate: string; toDate: string },
  today: string
): ReportingAnalytics {
  const base = records.filter(isBaseTransaction);
  return {
    kpis: computeKpis(records),
    trend: computeTrend(records, range.fromDate, range.toDate),
    byStatus: groupByStatus(base),
    byProduct: groupBy(
      base,
      r => r.product,
      r => r.product
    ),
    byCarrier: groupBy(
      base,
      r => r.carrierId,
      r => r.carrierName
    ),
    byBroker: groupBy(
      base,
      r => r.brokerId,
      r => r.brokerAgency
    ),
    byRiskTier: groupBy(
      base,
      r => r.riskTier,
      r => RISK_TIER_LABELS[r.riskTier] ?? r.riskTier,
      0
    ),
    byState: groupBy(
      base,
      r => r.riskState,
      r => r.riskState,
      8
    ),
    renewalOutlook: computeRenewalOutlook(base, today),
  };
}
